import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  IconButton,
} from '@mui/material';
import {
  Close,
  FavoriteBorder,
  Home,
  Logout,
  Menu,
  MessageOutlined,
  PersonOutline,
  Search,
  Wallet as WalletIcon,
  History as HistoryIcon,
  CloudUpload as UploadIcon,
  QrCode2 as QRIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Schedule as PendingIcon,
  VerifiedUser,
  Settings,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import api from '../services/api';
import { toast } from 'react-toastify';
import HeaderProfileMenu from '../components/HeaderProfileMenu';
import '../styles/maandvo-dashboard.css';

const MAROON = '#7b0011';

interface QRConfig {
  id: number;
  qr_code_data: string;
  description: string;
}

interface RechargeRequest {
  id: number;
  amount: number;
  transaction_id: string;
  receipt_image_url: string;
  payment_method: string;
  payment_date: string;
  status: string;
  rejection_reason?: string;
  created_at: string;
  approved_at?: string;
}

interface SpendHistoryItem {
  id: number;
  amount_inr: number;
  description: string;
  target_profile_id?: string;
  created_at: string;
}

interface WalletRules {
  items: Array<{
    rule_key: string;
    label: string;
    amount_inr: number;
    helper_text?: string;
    is_core: boolean;
    is_enabled: boolean;
    display_order: number;
  }>;
  is_user_specific_lead_cost: boolean;
}

const WalletPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [qrCode, setQrCode] = useState<QRConfig | null>(null);
  const [walletRules, setWalletRules] = useState<WalletRules | null>(null);
  const [rechargeHistory, setRechargeHistory] = useState<RechargeRequest[]>([]);
  const [rechargeHistoryPage, setRechargeHistoryPage] = useState(1);
  const [rechargeHistoryTotal, setRechargeHistoryTotal] = useState(0);
  const [spendHistory, setSpendHistory] = useState<SpendHistoryItem[]>([]);
  const [spendHistoryPage, setSpendHistoryPage] = useState(1);
  const [spendHistoryTotal, setSpendHistoryTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [receiptFileName, setReceiptFileName] = useState('');

  const [formData, setFormData] = useState({
    amount: '',
    transaction_id: '',
    payment_method: 'UPI',
    payment_date: new Date().toISOString().split('T')[0],
    receipt_image_url: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchWalletData();
  }, [user, navigate, rechargeHistoryPage, spendHistoryPage]);

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const [walletResponse, qrResponse, historyResponse, rulesResponse, spendResponse] = await Promise.all([
        api.get('/wallet/balance'),
        api.get('/wallet/qr-code'),
        api.get('/wallet/recharge-history', { params: { page: rechargeHistoryPage, limit: 20 } }),
        api.get('/wallet/rules'),
        api.get('/wallet/spend-history', { params: { page: spendHistoryPage, limit: 20 } }),
      ]);

      setWalletBalance(walletResponse.data.balance || 0);
      setQrCode(qrResponse.data.data || null);
      setRechargeHistory(historyResponse.data.data || []);
      setRechargeHistoryTotal(historyResponse.data.total || 0);
      setWalletRules(rulesResponse.data || null);
      setSpendHistory(spendResponse.data.data || []);
      setSpendHistoryTotal(spendResponse.data.total || 0);
    } catch (error: any) {
      console.error('Failed to fetch wallet data:', error);
      toast.error('Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleReceiptUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload a valid image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setFormData((prev) => ({ ...prev, receipt_image_url: result }));
      setReceiptFileName(file.name);
    };
    reader.onerror = () => {
      toast.error('Failed to read receipt image');
    };
    reader.readAsDataURL(file);
  };

  const handleSubmitRecharge = async () => {
    if (!formData.amount || !formData.transaction_id || !formData.payment_method || !formData.receipt_image_url) {
      toast.error('Please fill in all required fields, including receipt image');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/wallet/recharge-request', {
        amount: parseFloat(formData.amount),
        transaction_id: formData.transaction_id,
        payment_method: formData.payment_method,
        payment_date: new Date(formData.payment_date).toISOString(),
        receipt_image_url: formData.receipt_image_url,
      });

      toast.success('Recharge request submitted! Waiting for admin approval.');
      setDialogOpen(false);
      setFormData({
        amount: '',
        transaction_id: '',
        payment_method: 'UPI',
        payment_date: new Date().toISOString().split('T')[0],
        receipt_image_url: '',
      });
      setReceiptFileName('');
      await fetchWalletData();
    } catch (error: any) {
      console.error('Failed to submit recharge:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit recharge request');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'PENDING':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toUpperCase()) {
      case 'APPROVED':
        return <ApprovedIcon sx={{ fontSize: 16, mr: 0.5 }} />;
      case 'REJECTED':
        return <RejectedIcon sx={{ fontSize: 16, mr: 0.5 }} />;
      case 'PENDING':
        return <PendingIcon sx={{ fontSize: 16, mr: 0.5 }} />;
      default:
        return null;
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const sidebarItems = useMemo(
    () => [
      { label: 'Dashboard', icon: <Home fontSize="small" />, action: () => navigate('/my-profile') },
      { label: 'Update Profile', icon: <PersonOutline fontSize="small" />, action: () => navigate('/my-profile?openCompleteProfile=1') },
      { label: 'Match Preferences', icon: <Settings fontSize="small" />, action: () => navigate('/my-profile?openMatchPreferences=1') },
      { label: 'Search Profiles', icon: <Home fontSize="small" />, action: () => navigate('/') },
      { label: 'Unlocked Profiles', icon: <FavoriteBorder fontSize="small" />, action: () => navigate('/my-connections') },
      { label: 'Recharge Account', icon: <WalletIcon fontSize="small" />, action: () => navigate('/wallet'), active: true },
      { label: 'Activation Status', icon: <VerifiedUser fontSize="small" />, action: () => navigate('/activation-status') },
      { label: 'Change Password', icon: <Settings fontSize="small" />, action: () => navigate('/change-password') },
      { label: 'Logout', icon: <Logout fontSize="small" />, action: handleLogout },
    ],
    [navigate]
  );

  const sideBarContent = (
    <Box className="maandvo-dashboard-sidebar" role="presentation">
      <Box className="maandvo-dashboard-brand">
        <Box component="img" src="/maandvo-logo.png" alt="Brand" />
      </Box>
      <Box className="maandvo-dashboard-nav">
        {sidebarItems.map((item) => (
          <Box key={item.label} className={`maandvo-dashboard-nav-item ${item.active ? 'active' : ''}`} onClick={item.action}>
            {item.icon}
            <span>{item.label}</span>
          </Box>
        ))}
      </Box>
      <Box className="maandvo-dashboard-premium">
        <Typography sx={{ color: '#ffd89a', fontWeight: 800, mb: 0.7 }}>Account Balance</Typography>
        <Typography sx={{ color: '#fff6ea', fontSize: '0.9rem', mb: 1.2 }}>
          {walletBalance.toFixed(2)} Points available
        </Typography>
        <Button fullWidth variant="contained" sx={{ backgroundColor: '#ffb935', color: '#5d000d', fontWeight: 800 }} onClick={() => navigate('/wallet')}>
          Recharge Account
        </Button>
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Box className="maandvo-dashboard">
        <Box sx={{ py: 10, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress sx={{ color: MAROON }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box className="maandvo-dashboard">
      <Box className="maandvo-dashboard-mobile-top">
        <IconButton onClick={() => setMobileOpen(true)} sx={{ color: 'inherit' }}>
          <Menu />
        </IconButton>
        <Typography sx={{ fontWeight: 800 }}>Account</Typography>
        <HeaderProfileMenu
          initial={user?.first_name?.[0] || 'U'}
          actions={[
            { label: 'Dashboard', onClick: () => navigate('/my-profile') },
            { label: 'Logout', onClick: handleLogout, icon: <Logout fontSize="small" />, color: '#b71c1c' },
          ]}
        />
      </Box>

      <Box className="maandvo-dashboard-layout">
        <Box className="maandvo-dashboard-sidebar desktop-only">{sideBarContent}</Box>
        <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)} sx={{ display: { xs: 'block', md: 'none' } }}>
          <Box sx={{ width: 280 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
              <IconButton onClick={() => setMobileOpen(false)}>
                <Close />
              </IconButton>
            </Box>
            {sideBarContent}
          </Box>
        </Drawer>

        <Box className="maandvo-dashboard-main">
        <Box
          sx={{
            background: 'linear-gradient(135deg, #7b0011 0%, #9b1c2e 48%, #f4dcc3 100%)',
            color: 'white',
            borderRadius: 4,
            p: { xs: 2.5, md: 3.5 },
            mb: 3,
            boxShadow: '0 12px 30px rgba(78, 0, 11, 0.18)',
          }}
        >
          <Typography sx={{ fontSize: { xs: '1.8rem', md: '2.3rem' }, fontWeight: 900, lineHeight: 1.05 }}>
            Account & Recharge
          </Typography>
          <Typography sx={{ mt: 1, maxWidth: 680, color: 'rgba(255,255,255,0.92)' }}>
            Add points, track approvals, and manage your spend history from one polished account hub.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 4, border: '1px solid #eddac2', boxShadow: '0 8px 24px rgba(78, 0, 11, 0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="overline" sx={{ color: '#8c6a52', fontWeight: 800 }}>
                  Current Balance
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5 }}>
                  <WalletIcon sx={{ fontSize: 34, color: '#7b0011' }} />
                  <Box>
                    <Typography sx={{ color: '#7b0011', fontSize: '2rem', fontWeight: 900, lineHeight: 1 }}>
                      {walletBalance.toFixed(2)} Points
                    </Typography>
                    <Typography sx={{ color: '#7c6c63', fontSize: '0.9rem' }}>
                      Available account balance
                    </Typography>
                  </Box>
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2.5, backgroundColor: '#7b0011', py: 1.1, '&:hover': { backgroundColor: '#5f000e' } }}
                  onClick={() => {
                    setReceiptFileName('');
                    setDialogOpen(true);
                  }}
                >
                  Submit Payment Receipt
                </Button>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 4, border: '1px solid #eddac2', boxShadow: '0 8px 24px rgba(78, 0, 11, 0.08)', mt: 2.5 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography sx={{ color: '#7b0011', fontWeight: 900, mb: 1.5 }}>How it works</Typography>
                <Box sx={{ display: 'grid', gap: 1.5 }}>
                  {[
                    ['Scan the QR', 'Use any UPI app to complete the payment.'],
                    ['Upload the receipt', 'Attach the screenshot and transaction ID.'],
                    ['Wait for approval', 'Admin verifies and credits your account.'],
                  ].map(([title, description], index) => (
                    <Box key={title} sx={{ display: 'flex', gap: 1.2, alignItems: 'flex-start' }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: '#f5dfca', color: '#7b0011', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, flexShrink: 0 }}>
                        {index + 1}
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 800, color: '#3a2b24', fontSize: '0.95rem' }}>{title}</Typography>
                        <Typography sx={{ color: '#7c6c63', fontSize: '0.85rem' }}>{description}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card sx={{ borderRadius: 4, border: '1px solid #eddac2', boxShadow: '0 8px 24px rgba(78, 0, 11, 0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <QRIcon sx={{ color: '#7b0011' }} />
                  <Typography sx={{ fontWeight: 900, color: '#7b0011' }}>Scan to Pay</Typography>
                </Box>
                {qrCode ? (
                  <>
                    <Box sx={{ background: 'linear-gradient(135deg, #fff7ec 0%, #f8e5cf 100%)', borderRadius: 3, p: 2, textAlign: 'center', border: '1px solid #efd9bf' }}>
                      <img
                        src={qrCode.qr_code_data}
                        alt="Payment QR Code"
                        style={{ maxWidth: '100%', height: 'auto', borderRadius: 18, border: '1px solid #e2c3a4' }}
                      />
                    </Box>
                    {qrCode.description && (
                      <Typography sx={{ color: '#7b6c64', mt: 2, fontSize: '0.9rem' }}>{qrCode.description}</Typography>
                    )}
                  </>
                ) : (
                  <Alert severity="info">No QR code configured yet. Please contact admin.</Alert>
                )}
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 4, border: '1px solid #eddac2', boxShadow: '0 8px 24px rgba(78, 0, 11, 0.08)', mt: 2.5 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography sx={{ fontWeight: 900, color: '#7b0011', mb: 1.5 }}>Account Rules</Typography>
                <Box sx={{ display: 'grid', gap: 1.1 }}>
                  {(walletRules?.items || []).map((rule) => (
                    <Box key={rule.rule_key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, p: 1.1, borderRadius: 2, backgroundColor: '#fffaf5', border: '1px solid #f0e1ce' }}>
                      <Typography sx={{ color: '#5f5753', fontSize: '0.9rem' }}>{rule.label}</Typography>
                      <Typography sx={{ color: '#7b0011', fontWeight: 800 }}>{rule.amount_inr} Points</Typography>
                    </Box>
                  ))}
                </Box>
                {walletRules?.is_user_specific_lead_cost && (
                  <Typography sx={{ color: '#7b6c64', fontSize: '0.78rem', mt: 1.2 }}>
                    Your lead cost is customized by admin for contact unlock pricing.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ borderRadius: 4, border: '1px solid #eddac2', boxShadow: '0 8px 24px rgba(78, 0, 11, 0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography sx={{ fontWeight: 900, color: '#7b0011', mb: 1.5 }}>
                  Recent Recharge Status
                </Typography>
                <Box sx={{ display: 'grid', gap: 1.1 }}>
                  {rechargeHistory.slice(0, 3).map((request) => (
                    <Box key={`summary-${request.id}`} sx={{ p: 1.2, borderRadius: 2, border: '1px solid #f0e1ce', backgroundColor: '#fffdfa' }}>
                      <Typography sx={{ color: '#3a2b24', fontWeight: 800, fontSize: '0.92rem' }}>
                        {request.amount.toFixed(2)} Points
                      </Typography>
                      <Typography sx={{ color: '#7b6c64', fontSize: '0.8rem' }}>{request.transaction_id}</Typography>
                      <Chip size="small" label={request.status.toUpperCase()} color={getStatusColor(request.status)} sx={{ mt: 0.8 }} />
                    </Box>
                  ))}
                  {!rechargeHistory.length && (
                    <Alert severity="info">No recharge history yet.</Alert>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <HistoryIcon sx={{ color: '#7b0011' }} />
            <Typography variant="h6" sx={{ fontWeight: 900, color: '#7b0011' }}>
              Recharge History
            </Typography>
          </Box>

          {rechargeHistory.length === 0 ? (
            <Alert severity="info">No recharge requests yet. Click &quot;Submit Payment Receipt&quot; to get started!</Alert>
          ) : (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              {rechargeHistory.map((request) => (
                <Card key={request.id} sx={{ borderRadius: 3, border: '1px solid #eddac2', boxShadow: '0 6px 18px rgba(78, 0, 11, 0.06)' }}>
                  <CardContent sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.8fr 1fr 0.8fr' }, gap: 1.5, alignItems: 'center' }}>
                    <Box>
                      <Typography sx={{ color: '#7b0011', fontWeight: 900, fontSize: '1rem' }}>
                        {request.amount.toFixed(2)} Points
                      </Typography>
                      <Typography sx={{ color: '#7c6c63', fontSize: '0.84rem' }}>{new Date(request.created_at).toLocaleDateString()}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#7c6c63', fontSize: '0.8rem' }}>Transaction ID</Typography>
                      <Typography sx={{ fontFamily: 'monospace', color: '#3a2b24', wordBreak: 'break-word' }}>{request.transaction_id}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#7c6c63', fontSize: '0.8rem' }}>Method</Typography>
                      <Chip label={request.payment_method} size="small" variant="outlined" sx={{ mt: 0.8 }} />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        {getStatusIcon(request.status)}
                        <Chip label={request.status.toUpperCase()} color={getStatusColor(request.status)} size="small" />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 0.5 }}>
                <Button size="small" disabled={rechargeHistoryPage === 1} onClick={() => setRechargeHistoryPage((page) => Math.max(1, page - 1))}>Previous</Button>
                <Button size="small" disabled={rechargeHistoryPage * 20 >= rechargeHistoryTotal} onClick={() => setRechargeHistoryPage((page) => page + 1)}>Next</Button>
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon sx={{ color: '#7b0011' }} />
              <Typography variant="h6" sx={{ fontWeight: 900, color: '#7b0011' }}>
                Profile Unlock & Spend History
              </Typography>
            </Box>
            <Typography sx={{ color: '#7c6c63', fontSize: '0.85rem' }}>{spendHistoryTotal} total transactions</Typography>
          </Box>

          {spendHistory.length === 0 ? (
            <Alert severity="info">No profile unlock or account spend history yet.</Alert>
          ) : (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              {spendHistory.map((transaction) => (
                <Card key={transaction.id} onClick={() => transaction.target_profile_id && navigate(`/profile/${transaction.target_profile_id}`, { state: { from: '/wallet' } })} sx={{ borderRadius: 3, border: '1px solid #eddac2', boxShadow: '0 6px 18px rgba(78, 0, 11, 0.06)', cursor: transaction.target_profile_id ? 'pointer' : 'default', '&:hover': transaction.target_profile_id ? { borderColor: '#7b0011', backgroundColor: '#fffaf5' } : undefined }}>
                  <CardContent sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Box>
                      <Typography sx={{ color: '#7b0011', fontWeight: 900 }}>{transaction.description}</Typography>
                      <Typography sx={{ color: '#7c6c63', fontSize: '0.84rem', mt: 0.35 }}>{new Date(transaction.created_at).toLocaleString()}</Typography>
                    </Box>
                    <Typography sx={{ color: '#7b0011', fontWeight: 900 }}>-{transaction.amount_inr.toFixed(2)} Points</Typography>
                  </CardContent>
                </Card>
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 0.5 }}>
                <Button size="small" disabled={spendHistoryPage === 1} onClick={() => setSpendHistoryPage((page) => Math.max(1, page - 1))}>Previous</Button>
                <Button size="small" disabled={spendHistoryPage * 20 >= spendHistoryTotal} onClick={() => setSpendHistoryPage((page) => page + 1)}>Next</Button>
              </Box>
            </Box>
          )}
        </Box>
        </Box>
      </Box>

      {/* Recharge Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit Payment Receipt</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Alert severity="info">
            Make sure you have completed the payment via UPI before submitting the details.
          </Alert>

          <TextField
            label="Amount (Points)"
            name="amount"
            type="number"
            value={formData.amount}
            onChange={handleFormChange}
            fullWidth
            size="small"
            inputProps={{ min: '100', step: '1' }}
            required
          />

          <TextField
            label="Transaction ID / Reference"
            name="transaction_id"
            value={formData.transaction_id}
            onChange={handleFormChange}
            fullWidth
            size="small"
            placeholder="e.g., UPI1234567890"
            required
          />

          <TextField
            label="Payment Method"
            name="payment_method"
            select
            value={formData.payment_method}
            onChange={handleFormChange}
            fullWidth
            size="small"
            SelectProps={{
              native: true,
            }}
          >
            <option value="UPI">UPI</option>
            <option value="Bank Transfer">Bank Transfer</option>
            <option value="Card">Card</option>
          </TextField>

          <TextField
            label="Payment Date"
            name="payment_date"
            type="date"
            value={formData.payment_date}
            onChange={handleFormChange}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            required
          />

          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadIcon />}
            sx={{ alignSelf: 'flex-start' }}
          >
            Upload Receipt Image
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={handleReceiptUpload}
            />
          </Button>

          {receiptFileName && (
            <Typography variant="caption" color="textSecondary">
              Selected file: {receiptFileName}
            </Typography>
          )}

          {formData.receipt_image_url && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                Receipt Preview:
              </Typography>
              <img
                src={formData.receipt_image_url}
                alt="Receipt preview"
                style={{ maxWidth: '240px', borderRadius: '8px', border: '1px solid #ccc' }}
              />
            </Box>
          )}

          {!formData.receipt_image_url && (
            <Typography variant="caption" color="textSecondary">
              Upload your payment screenshot to continue.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmitRecharge}
            variant="contained"
            sx={{ backgroundColor: '#E63946' }}
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WalletPage;
